import { useMemo, useState } from "react";
import { KeyRound } from "lucide-react";
import type {
  CyFunRequirement,
  MaturityEvaluationRubric,
} from "@/types/maturity";
import { LevelButtons } from "../LevelButtons";
import { LevelDescriptionPanel } from "../LevelDescriptionPanel";

type RequirementScoreRowProps = {
  requirement: CyFunRequirement;
  isKeyMeasure: boolean;
  docScore: number | undefined;
  implScore: number | undefined;
  docNote: string;
  implNote: string;
  rubrics: MaturityEvaluationRubric[];
  onScoreChange: (key: string, value: number) => void;
  onNoteChange: (key: string, value: string) => void;
  onNoteBlur: () => void;
};

function RequirementScoreRow({
  requirement,
  isKeyMeasure,
  docScore,
  implScore,
  docNote,
  implNote,
  rubrics,
  onScoreChange,
  onNoteChange,
  onNoteBlur,
}: RequirementScoreRowProps) {
  const docKey = `${requirement.code}-D`;
  const implKey = `${requirement.code}-I`;

  const [docRevealed, setDocRevealed] = useState(() => docScore !== undefined);
  const [implRevealed, setImplRevealed] = useState(
    () => implScore !== undefined
  );

  const showDocNote = docScore !== undefined && (docRevealed || docNote !== "");
  const showImplNote =
    implScore !== undefined && (implRevealed || implNote !== "");

  const docRubric = useMemo(
    () =>
      docScore !== undefined
        ? rubrics.find(
            (r) =>
              r.requirement_code === requirement.code &&
              r.maturity_level === docScore
          )
        : undefined,
    [rubrics, requirement.code, docScore]
  );

  const implRubric = useMemo(
    () =>
      implScore !== undefined
        ? rubrics.find(
            (r) =>
              r.requirement_code === requirement.code &&
              r.maturity_level === implScore
          )
        : undefined,
    [rubrics, requirement.code, implScore]
  );

  return (
    <div className="py-3 border-b border-slate-100 last:border-b-0">
      <div className="flex items-start gap-2 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono font-semibold text-slate-400">
              {requirement.code}
            </span>
            {isKeyMeasure && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold font-mono bg-amber-100 text-amber-700">
                <KeyRound className="w-3 h-3" />
                Măsură cheie
              </span>
            )}
          </div>
          <p className="text-sm text-slate-700 mt-0.5 leading-snug">
            {requirement.name}
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <div
          className="rounded-lg border border-slate-100 p-3"
          style={{ background: "#FAFBFC" }}
        >
          <div className="flex items-center gap-2 mb-2.5">
            <span className="text-[9px] font-bold font-mono tracking-[0.8px] uppercase text-slate-500 bg-slate-200 px-2 py-1 rounded">
              Documentație
            </span>
          </div>
          <LevelButtons
            selectedLevel={docScore}
            onSelect={(level) => {
              setDocRevealed(true);
              onScoreChange(docKey, level);
            }}
          />
          {showDocNote && (
            <>
              <LevelDescriptionPanel
                rubric={docRubric}
                level={docScore}
                showSection="doc"
              />
              <textarea
                className="mt-2 w-full text-sm rounded border border-slate-200 p-2 resize-none min-h-[72px] focus:outline-none focus:ring-1 focus:ring-slate-300 placeholder:text-slate-400 bg-white"
                placeholder="Justificare (opțional)..."
                value={docNote}
                onChange={(e) => onNoteChange(docKey, e.target.value)}
                onBlur={onNoteBlur}
              />
            </>
          )}
        </div>

        <div
          className="rounded-lg border border-blue-100 p-3"
          style={{ background: "#F0F9FF" }}
        >
          <div className="flex items-center gap-2 mb-2.5">
            <span className="text-[9px] font-bold font-mono tracking-[0.8px] uppercase text-blue-700 bg-blue-100 px-2 py-1 rounded">
              Implementare
            </span>
          </div>
          <LevelButtons
            selectedLevel={implScore}
            onSelect={(level) => {
              setImplRevealed(true);
              onScoreChange(implKey, level);
            }}
          />
          {showImplNote && (
            <>
              <LevelDescriptionPanel
                rubric={implRubric}
                level={implScore}
                showSection="impl"
              />
              <textarea
                className="mt-2 w-full text-sm rounded border border-blue-100 p-2 resize-none min-h-[72px] focus:outline-none focus:ring-1 focus:ring-blue-200 placeholder:text-slate-400 bg-white"
                placeholder="Justificare (opțional)..."
                value={implNote}
                onChange={(e) => onNoteChange(implKey, e.target.value)}
                onBlur={onNoteBlur}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default RequirementScoreRow;
